import { Product } from '@portal/portal-core';
import { getContactEmail } from 'constants/contact-emails';
import { PRODUCT_INFO } from 'constants/products';
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { selectActiveTab } from 'selectors/product-tab';
import styled from 'styled-components';

import * as utils from 'utils';
import BDRCTab from './product-tabs/bdrc-tab';
import FacsWorkflowManagerTab from './product-tabs/facs-workflow-manager-tab';
import FlowJoTab from './product-tabs/flowjo-tab';
import InfinicytTab from './product-tabs/infinicyt-tab';
import SeqGeqTab from './product-tabs/seq-geq-tab';

const { NAMES_UI_TABS } = Product;

// Minimal shape of the footer links consumed here (see ProductLinks in constants/products).
interface ProductLinks {
  learnMore?: string;
  documentation?: string;
}

interface ProductContent {
  main: ReactElement;
  links: ProductLinks;
}

const SelectedTabContent = (): ReactElement | null => {
  // baseSelect selectors resolve to `unknown`; the active tab is always a UI-tab string.
  const activeTab = useSelector(selectActiveTab) as string;

  const getProductContent = (): ProductContent | null => {
    const { links } = PRODUCT_INFO[activeTab];

    switch (activeTab) {
      case NAMES_UI_TABS.flowJo:
        return {
          main: <FlowJoTab />,
          links,
        };
      case NAMES_UI_TABS.seqGeq:
        return {
          main: <SeqGeqTab />,
          links,
        };
      case NAMES_UI_TABS.bdrc:
        return {
          main: <BDRCTab />,
          links,
        };
      case NAMES_UI_TABS.infinicytIvd:
        return {
          main: <InfinicytTab productName={NAMES_UI_TABS.infinicytIvd} />,
          links,
        };
      case NAMES_UI_TABS.infinicytRuo:
        return {
          main: <InfinicytTab productName={NAMES_UI_TABS.infinicytRuo} />,
          links,
        };
      case NAMES_UI_TABS.infinicytEa:
        return {
          main: <InfinicytTab productName={NAMES_UI_TABS.infinicytEa} />,
          links,
        };
      case NAMES_UI_TABS.infinicytIuo:
        return {
          main: <InfinicytTab productName={NAMES_UI_TABS.infinicytIuo} />,
          links,
        };
      case NAMES_UI_TABS.infinicytUsIvd:
        return {
          main: <InfinicytTab productName={NAMES_UI_TABS.infinicytUsIvd} />,
          links,
        };
      case NAMES_UI_TABS.facsWorkflowManager:
        return {
          main: <FacsWorkflowManagerTab />,
          links,
        };
      default:
        return null;
    }
  };

  // the research use only notice should be centered on the page and the font size should be slightly larger than the footer links
  const renderResearchUseOnlyNotice = (): ReactElement | null => {
    if (activeTab === NAMES_UI_TABS.infinicytRuo) {
      return (
        <div
          style={{
            textAlign: 'center',
            fontSize: '18px',
            marginBottom: '40px',
            fontWeight: 'bold',
          }}
        >
          For Research Use Only. Not for use in diagnostic or therapeutic procedures.
        </div>
      );
    }
    return null;
  };

  const renderLinkFooter = (productLinks: ProductLinks): ReactElement => {
    const chinaFlag = utils.chinaFlag();
    const newsletterLink = chinaFlag
      ? 'http://mp.weixin.qq.com/mp/homepage?__biz=Mzg5ODY0ODkzOA==&hid=2&sn=f242fa2e27a2c1cc38a314c6379125f5&scene=18#wechat_redirect'
      : 'https://www.flowjo.com/newsletter';
    const learnMoreLink = chinaFlag
      ? 'https://bdbiosciences.com/zh-cn/products/software/flowjo-v10-software'
      : productLinks.learnMore;

    // if the activeTab includes the Infinicyt substring, omit the Newsletter link
    const Newsletter = !activeTab.toLowerCase().includes('infinicyt') ? (
      <Link href={newsletterLink} rel="noopener noreferrer" target="_blank">
        Newsletter
      </Link>
    ) : null;

    return (
      <Footer>
        <Link href={learnMoreLink} rel="noopener noreferrer" target="_blank">
          Learn More
        </Link>
        <Link href={productLinks.documentation} rel="noopener noreferrer" target="_blank">
          Portal Documentation
        </Link>
        <Link
          href={`mailto:${getContactEmail(activeTab)}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          Contact Us
        </Link>
        {Newsletter}
      </Footer>
    );
  };

  const content = getProductContent();
  if (!content) {
    return null;
  }
  return (
    <ContentWrapper>
      <MainContent>{content.main}</MainContent>
      {renderResearchUseOnlyNotice()}
      {renderLinkFooter(content.links)}
    </ContentWrapper>
  );
};

export default SelectedTabContent;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${(props) => props.theme.colors.white};
`;

const MainContent = styled.div`
  display: flex;
  flex: 1 0 auto;
  min-height: fit-content;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  padding: 0px 10px 20px;

  ${({ theme: { minWidth } }) => minWidth('md')} {
    padding: 0 10px 10px;
  }
`;

const Link = styled.a`
  font-size: 16px;
  color: ${({ theme: { colors } }) => colors.black};
`;
